import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle, Badge, Button, PageSkeleton } from '@/components/ui';
import { JsonViewer } from '@/components/ui/json-viewer';
import { ApiErrorDisplay } from '@/components/features/error';
import { useWebhook } from '@/hooks/use-webhooks';
import { formatDate } from '@/lib/utils';
import type { WebhookStatus } from '@/types/webhook';

/**
 * Get badge variant for webhook status.
 */
function getStatusBadgeVariant(status: WebhookStatus) {
  switch (status) {
    case 'received':
      return 'info';
    case 'processed':
      return 'success';
    case 'failed':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Get status icon.
 */
function StatusIcon({ status }: { status: WebhookStatus }) {
  switch (status) {
    case 'received':
      return <Clock className="h-4 w-4 text-blue-500" />;
    case 'processed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-destructive" />;
    default:
      return <AlertCircle className="h-4 w-4" />;
  }
}

/**
 * Detail row component for displaying key-value pairs.
 */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-4 py-2 border-b border-border last:border-0">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/**
 * Webhook detail page.
 * Route: /webhooks/:id
 */
export function WebhookDetailPage() {
  const { t } = useTranslation('webhooks');
  const { id } = useParams<{ id: string }>();
  const { data: webhook, isLoading, error, refetch } = useWebhook(id || '');

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (error) {
    return (
      <div>
        <PageHeader title={t('detail.title')} />
        <ApiErrorDisplay error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  if (!webhook) {
    return (
      <div>
        <PageHeader title={t('detail.notFound')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('detail.notFoundDescription')}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/webhooks" aria-label={t('detail.backToList')}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight flex-1">{t('detail.title')}</h1>
      </div>

      {/* Overview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StatusIcon status={webhook.status} />
            {webhook.eventId}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl>
            <DetailRow label={t('detail.eventType')}>
              <Badge variant="outline">{t(`eventTypes.${webhook.eventType}`)}</Badge>
            </DetailRow>
            <DetailRow label={t('detail.status')}>
              <Badge variant={getStatusBadgeVariant(webhook.status)}>
                {t(`common:status.${webhook.status}`)}
              </Badge>
            </DetailRow>
            <DetailRow label={t('detail.transactionId')}>
              <code className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">
                {webhook.transactionId}
              </code>
            </DetailRow>
            <DetailRow label={t('detail.merchantId')}>
              <code className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">
                {webhook.merchantId}
              </code>
            </DetailRow>
            <DetailRow label={t('detail.received')}>
              {formatDate(webhook.receivedAt)}
            </DetailRow>
            {webhook.processedAt && (
              <DetailRow label={t('detail.processed')}>
                {formatDate(webhook.processedAt)}
              </DetailRow>
            )}
            {webhook.errorMessage && (
              <DetailRow label={t('detail.error')}>
                <span className="text-destructive">{webhook.errorMessage}</span>
              </DetailRow>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Payload Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t('detail.payload')}</CardTitle>
        </CardHeader>
        <CardContent>
          <JsonViewer data={webhook.payload} maxHeight="500px" />
        </CardContent>
      </Card>

      {/* Headers Card (if available) */}
      {webhook.headers && Object.keys(webhook.headers).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.httpHeaders')}</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonViewer data={webhook.headers} maxHeight="300px" />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
